
import type { ComputedRef, PropType } from 'vue'
import type { Equalize, EqualizeBoostingField } from '../../modules/equalize'
import type { EqualizeDomainService } from '../../services/interfaces'

import { computed, defineComponent, getCurrentInstance } from 'vue'

import { mapGetters } from '../../composables/utils'

import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'

import { ColorEnum, EqualizeBoostingActionEnum } from '../../enums'

import { capitalizeSentence } from '../../filters'

import { CONFIG_CONSTS } from '../../store/config/config.consts'

import { EpbSlide } from '../shared/epb-slide'
import EqualizeFeatureContainer from './equalize-feature-container.component.vue'

export interface SetupValues {
  baseClass: string
  domainService: EqualizeDomainService
  defaultButtonDisabled: ComputedRef<boolean>
  getSliderConfig: (boost: EqualizeBoostingField) => {
    min: number
    max: number
    color: ColorEnum
  }
  setDefaultValues: () => void
  getPrettyName: (fieldConfig: EqualizeBoostingField) => string
  ColorEnum
}

export default defineComponent({
  name: 'BoostingFeatureContainer',
  components: {
    EpbSlide,
    EqualizeFeatureContainer,
  },
  props: {
    item: {
      type: Object as PropType<Equalize>,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    disabled: Boolean,
    messages: {
      type: Object,
      required: true,
    },
  },
  setup(props: { item: Equalize; readonly: boolean; messages: object }): SetupValues {
    const {
      proxy: { $store },
    } = getCurrentInstance()

    const domainService = CONTAINER.get<EqualizeDomainService>(TYPES.EqualizeDomainService)

    const defaultButtonDisabled = computed((): boolean => {
      return Object.keys(props.item.defaultFeatures).length
        ? domainService.hasBoostingDefaultValues(props.item.features, props.item.defaultFeatures)
        : false
    })

    const { instanceConfig } = mapGetters($store, CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance,
    })

    const getSliderConfig = (
      boost: EqualizeBoostingField,
    ): { min: number; max: number; color: ColorEnum } => {
      if (boost.action === EqualizeBoostingActionEnum.BURY) {
        return {
          min: -100,
          max: 0,
          color: ColorEnum.RED_75,
        }
      }
      return {
        min: 0,
        max: 100,
        color: ColorEnum.GREEN_75,
      }
    }

    const getPrettyName = (fieldConfig: EqualizeBoostingField): string => {
      const catalogFieldConfig = instanceConfig.value.catalog.fields.find(
        ({ id }) => id === fieldConfig.field,
      )
      const prettyField: string = catalogFieldConfig?.prettyName ?? fieldConfig.field

      return capitalizeSentence(`${prettyField} ${fieldConfig.value}`)
    }

    const baseClass = 'epb-boosting-feature-container'

    const setDefaultValues = (): void => {
      domainService.setDefaultByFeature('boosting', props.item.features, props.item.defaultFeatures)
    }

    return {
      baseClass,
      domainService,
      defaultButtonDisabled,
      setDefaultValues,
      getSliderConfig,
      getPrettyName,
      ColorEnum,
    }
  },
})
